import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { getStats, getEarningsByStream, type StreamSubtotal } from '@/api/fleet';
import {
  formatUsd,
  formatRev,
  formatHashrate,
  STREAM_META,
  PLATFORM_META
} from '@/lib/format';
import { InferenceWorkersBadge } from '@/components/InferenceWorkersBadge';
import { PendingInferencePanel } from '@/components/PendingInferencePanel';

const StatCard = ({
  label,
  value,
  sub,
  loading
}: {
  label: string;
  value?: string | number | null;
  sub?: string;
  loading?: boolean;
}) => (
  <div className="rounded-xl border border-nurev-border bg-nurev-card p-4 lg:p-5">
    <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-nurev-muted lg:text-xs">
      {label}
    </p>
    {loading ? (
      <div className="h-7 w-24 animate-pulse rounded bg-white/[0.04]" />
    ) : (
      <>
        <p className="text-lg font-bold leading-tight text-nurev-text lg:text-2xl">
          {value ?? '—'}
        </p>
        {sub && <p className="mt-0.5 text-[10px] text-nurev-muted lg:text-xs">{sub}</p>}
      </>
    )}
  </div>
);

export function OverviewScreen() {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['fleet-stats'],
    queryFn: () => getStats(),
    refetchInterval: 60_000
  });

  const { data: earnings, isLoading: earningsLoading } = useQuery({
    queryKey: ['fleet-earnings-30'],
    queryFn: () => getEarningsByStream(30)
  });

  const s = stats ?? {
    totalNodes: 0,
    activeNodes: 0,
    pausedNodes: 0,
    offlineNodes: 0,
    totalHashrate: 0,
    totalCpuCores: 0,
    totalRamMb: 0,
    avgCpuUsage: 0,
    avgMemUsageMb: 0,
    platformBreakdown: [],
    groupBreakdown: []
  };

  const activePct = s.totalNodes
    ? Math.round((s.activeNodes / s.totalNodes) * 100)
    : 0;

  const streams: StreamSubtotal[] = earnings?.byStream ?? [];
  const hasActivity = streams.some(st => st.usdAmount > 0);

  return (
    <div className="space-y-8">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-nurev-text">Fleet Overview</h1>
          <p className="mt-1 text-sm text-nurev-muted">
            Health + revenue snapshot across your hosting fleet.
          </p>
        </div>
        {earningsLoading ? (
          <div className="text-xs text-nurev-muted">Loading earnings…</div>
        ) : (
          <div className="text-right">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-nurev-muted">
              Last 30d revenue
            </div>
            <div className="text-2xl font-bold text-emerald-300">
              {formatUsd(earnings?.totals.usdAmount)}
            </div>
          </div>
        )}
      </header>

      {/* Inference worker setup badge — only renders if the operator has any
          inference workers connected; auto-hides when there are none. */}
      <InferenceWorkersBadge />

      {/* Pending inference earnings (real-time work-in-progress, settles
          on the batched hourly cron). Auto-hides for operators with no
          inference activity at all. */}
      <PendingInferencePanel />

      {/* KPI grid */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
        <StatCard label="Total Nodes" value={s.totalNodes} loading={statsLoading} />
        <StatCard
          label="Active"
          value={s.activeNodes}
          sub={s.totalNodes ? `${activePct}%` : undefined}
          loading={statsLoading}
        />
        <StatCard label="Offline" value={s.offlineNodes} loading={statsLoading} />
        <StatCard
          label="Speed"
          value={formatHashrate(s.totalHashrate)}
          loading={statsLoading}
        />
        <StatCard
          label="CPU Cores"
          value={s.totalCpuCores?.toLocaleString()}
          sub={s.avgCpuUsage ? `${Math.round(s.avgCpuUsage)}% avg` : undefined}
          loading={statsLoading}
        />
        <StatCard
          label="RAM"
          value={s.totalRamMb ? `${(s.totalRamMb / 1024).toFixed(1)} GB` : '0'}
          sub={
            s.avgMemUsageMb
              ? `${(s.avgMemUsageMb / 1024).toFixed(1)} GB avg`
              : undefined
          }
          loading={statsLoading}
        />
      </div>

      {/* Earnings by stream */}
      <section className="card">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-emerald-300">
            Earnings by Stream · Last 30d
          </h2>
          <span className="font-mono text-xs text-nurev-muted">
            Total: {formatUsd(earnings?.totals.usdAmount)}
            {earnings && earnings.totals.revAmount > 0
              ? ` · ${formatRev(earnings.totals.revAmount)} REV`
              : ''}
          </span>
        </div>

        {earningsLoading ? (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="h-20 animate-pulse rounded-lg bg-white/[0.04]"
              />
            ))}
          </div>
        ) : hasActivity ? (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
            {streams.map(st => {
              const meta = STREAM_META[st.stream];
              return (
                <div
                  key={st.stream}
                  className={`rounded-lg border border-nurev-border bg-gradient-to-br ${meta.accent} p-3`}
                >
                  <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-nurev-muted">
                    {meta.emoji} {meta.label}
                  </p>
                  <p className={`text-base font-bold leading-tight lg:text-lg ${meta.color}`}>
                    {formatUsd(st.usdAmount)}
                  </p>
                  <div className="mt-1.5 space-y-0.5 text-[10px] text-nurev-muted">
                    {st.revAmount > 0 && <p>{formatRev(st.revAmount)} REV</p>}
                    {st.distinctNodes > 0 && (
                      <p>
                        {st.distinctNodes} node{st.distinctNodes === 1 ? '' : 's'}
                        {st.blockCount > 0
                          ? ` · ${st.blockCount} block${st.blockCount === 1 ? '' : 's'}`
                          : ''}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-nurev-muted">
            No earnings credited in the last 30 days. Earnings populate when
            upstream revenue arrives (mining-pool block reward, Mysterium
            settlement, etc.) for nodes registered to this fleet.
          </p>
        )}
      </section>

      {/* Platform + Group breakdown */}
      {(s.platformBreakdown.length > 0 || s.groupBreakdown.length > 0) && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {s.platformBreakdown.length > 0 && (
            <section className="card">
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-emerald-300">
                By Platform
              </h2>
              <div className="space-y-2">
                {s.platformBreakdown.map(p => {
                  const meta =
                    PLATFORM_META[p._id as keyof typeof PLATFORM_META] || null;
                  return (
                    <div
                      key={p._id}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="text-nurev-text">
                        {meta?.emoji ?? '·'} {meta?.label ?? p._id}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-nurev-muted">
                          {p.count} nodes
                        </span>
                        <span className="font-mono text-xs text-emerald-300">
                          {formatHashrate(p.hashrate)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
          {s.groupBreakdown.length > 0 && (
            <section className="card">
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-emerald-300">
                By Group
              </h2>
              <div className="space-y-2">
                {s.groupBreakdown.map(g => (
                  <div
                    key={g._id}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-nurev-text">{g._id || '(default)'}</span>
                    <span className="text-xs text-nurev-muted">
                      {g.activeCount} / {g.count} active
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {/* Onboarding nudge — only when truly empty */}
      {!statsLoading && s.totalNodes === 0 && (
        <section className="rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-cyan-500/5 p-6">
          <h2 className="mb-2 text-lg font-bold text-nurev-text">
            No nodes yet. Let's fix that.
          </h2>
          <p className="mb-4 text-sm text-nurev-text-soft">
            Your fleet is empty. Mint a provisioning token, drop the Docker
            template (or systemd unit) on your first server, and it'll register
            itself + start earning within ~30 seconds.
          </p>
          <div className="flex flex-wrap gap-3 text-sm">
            <a href="/tokens" className="btn-primary">
              Mint a provisioning token →
            </a>
            <a
              href="https://docs.nurev.io/hosting"
              target="_blank"
              rel="noreferrer"
              className="btn-secondary"
            >
              <Loader2 className="hidden h-4 w-4" />
              Read the runbook
            </a>
          </div>
        </section>
      )}
    </div>
  );
}
